import React, { useCallback, useEffect, useRef, useState } from "react";
import SC from "./style";
import { Button, InnoTable } from "InnoUI/components";
import theme from "theme";
import { DatePicker, ConfigProvider } from "antd";
import { apiGet, apiPost } from "api/callApi";
import { innoTableConfig } from "utils/config";
import { useSearchParams } from "react-router-dom";
import { formatFileSize, readFromLocalStorage } from "utils/helper";
import TimezoneConverter from "components/TimeZoneConvertor";
import CustomBreadcrumb from "components/CustomBreadcrumb";
import InnoIcons from "@InnoIcons/";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { removeCart } from "@store/cart/actions";
import axios from "axios";
import SendCart from "./SendCart";
import CartOldFiles from "components/CartOldFiles";
import CartHistory from "./CartHistory";

const MyCart = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const projectIdParam = searchParams.get("projectId");
  const [selectedItems, setSelectedItems] = useState([]);
  const cartData = useSelector((state) => state.cartData?.data || []);
  const totalSize = useSelector((state) => state.cartData?.totalSize);
  const [openModalView, setOpenModalView] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [activeTab, setActiveTab] = useState("cart");
  const [historySearchInput, setHistorySearchInput] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  const searchDebounceRef = useRef(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyTotalRecords, setHistoryTotalRecords] = useState(0);
  const [cartHistoryCount, setCartHistoryCount] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(
    Number(innoTableConfig.pageSize) || 20,
  );
  const [sendTypeFilter, setSendTypeFilter] = useState(null);
  const [sortField, setSortField] = useState("createdOn");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleHistorySearchChange = (e) => {
    const value = e.target.value;
    setHistorySearchInput(value);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setHistoryPage(1);
      setHistorySearch(value);
    }, 400);
  };

  const handleHistorySearchClear = () => {
    setHistoryPage(1);
    setHistorySearchInput("");
    setHistorySearch("");
  };

  const [dateRange, setDateRange] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const historyDateFrom = dateRange?.[0]?.format("YYYY-MM-DD") || "";
  const historyDateTo = dateRange?.[1]?.format("YYYY-MM-DD") || "";

  const handleDateRangeChange = (dates) => {
    setHistoryPage(1);
    setDateRange(dates && dates[0] && dates[1] ? dates : null);
  };

  const hasActiveFilters = historySearchInput || dateRange;

  const handleResetFilters = () => {
    setHistoryPage(1);
    setHistorySearchInput("");
    setHistorySearch("");
    setDateRange(null);
    setResetKey((k) => k + 1);
  };

  const currentProject = useSelector(
    (state) => state?.currentProject?.data || [],
  );
  const effectiveProjectId = currentProject?.id || projectIdParam;
  const safeFileSize = (value) => {
    const size = Number(value);
    return Number.isFinite(size) ? size : 0;
  };

  const getCartHistory = useCallback(async () => {
    if (!effectiveProjectId) return;
    try {
      setHistoryLoading(true);
      const params = new URLSearchParams({
        page: historyPage,
        limit: Number(historyPageSize),
        sortField,
        sortOrder,
      });
      if (historySearch) params.set("search", historySearch);
      if (sendTypeFilter !== null) params.set("sendType", sendTypeFilter);
      if (historyDateFrom) params.set("dateFrom", historyDateFrom);
      if (historyDateTo) params.set("dateTo", historyDateTo);

      const response = await apiGet(
        `/v1/cart/history/${effectiveProjectId}?${params.toString()}`,
      );
      setHistoryData(response?.data || []);
      setHistoryTotalRecords(response?.pagination?.totalRecords || 0);
      setCartHistoryCount(response?.totalAccessHistory || 0);
    } catch (error) {
      console.log("Error fetching cart history", error);
    } finally {
      setHistoryLoading(false);
    }
  }, [
    effectiveProjectId,
    historyPage,
    historyPageSize,
    sortField,
    sortOrder,
    historySearch,
    sendTypeFilter,
    historyDateFrom,
    historyDateTo,
  ]);

  useEffect(() => {
    getCartHistory();
  }, [getCartHistory]);

  const onRowClick = (data) => {};

  const subColumnData = [
    {
      title: "Name of Document",
      dataIndex: "subject",
      sorter: (a, b) => a.subject.localeCompare(b.subject),
      showSorterTooltip: { target: "sorter-icon" },
      sortDirections: ["ascend", "descend"],
      onCell: (record) => ({
        style: { width: "350px", cursor: "pointer" },
      }),
      render(value, record) {
        return <SC.ValueSpanP>{value}</SC.ValueSpanP>;
      },
    },
    {
      title: "File Name",
      dataIndex: "fileName",
      onCell: (record) => ({
        style: { width: "350px", cursor: "pointer" },
      }),
      render(value, record) {
        return <SC.ValueSpanP>{value}</SC.ValueSpanP>;
      },
    },

    {
      title: "Size",
      dataIndex: "fileSize",
      onCell: () => ({
        style: { width: "150px" },
      }),
      render(value, record, index) {
        return <>{formatFileSize(safeFileSize(value))}</>;
      },
    },
    {
      title: "Added On",
      dataIndex: "createdOn",
      onCell: () => ({
        style: { width: "150px" },
      }),
      render(value, record, index) {
        return (
          <SC.ValueSpan>
            <TimezoneConverter time={value} />
          </SC.ValueSpan>
        );
      },
    },
  ];

  const tableConfig = {
    draggable: false,
    selection: true,
    expandable: true,
    sno: false,
    addNew: false,
    pagination: {
      pageSize: innoTableConfig.pageSize,
    },
    search: false,
    action: false,
    divBorder: false,
  };

  const checkboxChangeCallback = (data, state) => {
    if (state) {
      setSelectedItems((prevSelectedItems) => [...prevSelectedItems, data]);
    } else {
      const newData = selectedItems.filter((item) => data !== item);
      setSelectedItems(newData);
    }
  };

  const allCheckboxChangeCallback = (data, state) => {
    if (state) {
      setSelectedItems(data);
    } else {
      setSelectedItems([]);
    }
  };

  const handleRemoveCart = () => {
    if (selectedItems.length > 0) {
      const data = {
        id: effectiveProjectId,
        files: selectedItems,
      };
      dispatch(removeCart(data));
      setSelectedItems([]);
    }
  };

  const handleClearCart = () => {
    const data = {
      clear: 1,
      id: effectiveProjectId,
    };
    dispatch(removeCart(data));
  };

  const {
    REACT_APP_BASE_URL: baseURL,
    REACT_APP_REQUEST_TIMEOUT: timeout,
    REACT_APP_API_KEY,
  } = process.env;

  let accessToken = REACT_APP_API_KEY;
  let userAuth = readFromLocalStorage("userAccessToken");
  let BASE_URL = baseURL || `${window.location.origin}/api`;
  userAuth = userAuth === null ? "" : userAuth;
  const requestTimeout = timeout ? parseInt(timeout, 10) * 1000 : 180000;
  const handleDownload = async () => {
    try {
      setIsLoading(true);
      if (selectedItems.length > 1) {
        const response = await axios.post(
          `${BASE_URL}/v1/download`,
          { files: selectedItems, fileName: currentProject.propertyName },
          {
            responseType: "blob", // Move this here
            headers: {
              api_key: accessToken,
              Authorization: `Bearer ${userAuth}`,
            },
            timeout: requestTimeout,
          },
        );

        const blob = new Blob([response.data], { type: "application/zip" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download =
          `${currentProject.propertyName}_${new Date().toDateString()}` ||
          "files"; // Explicitly set the .zip extension
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href); // Clean up memory
        setIsLoading(false);
        setSelectedItems([]);
      } else {
        setIsLoading(true);
        const response = await apiPost(`/v1/download`, {
          files: selectedItems,
          fileName: currentProject.propertyName,
        });
        let url = response.fileUrl;
        fetch(url)
          .then((res) => {
            if (!res.ok) throw new Error("Failed to fetch the file.");
            return res.blob();
          })
          .then((blob) => {
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = response.fileName || "downloaded-file";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
            setIsLoading(false);
            setSelectedItems([]);
          })

          .catch((error) => {
            console.error("Error fetching the file:", error);
          });
      }
    } catch (error) {
      console.log("Error downloading the file: " + error.message);
    }
  };

  const selctedFileSize =
    selectedItems?.length > 0
      ? cartData.filter((item) => selectedItems.includes(item.id))
      : [];

  const totalFileSize = selctedFileSize.reduce(
    (sum, file) => sum + safeFileSize(file.fileSize),
    0,
  );
  const safeTotalSize = safeFileSize(totalSize);

  const handleSend = () => {
    setOpenModalView(true);
  };

  return (
    <>
      <CustomBreadcrumb />

      <div
        style={{
          display: "flex",
          // flex: 1,
          alignItems: "center",
          padding: "15px 25px 5px 0px",
          borderBottom: `1px solid ${theme.colors.bordertask}`,
          justifyContent: "space-between",
          marginTop: "50px",
        }}
      >
        <SC.TabDiv>
          <SC.HeaderContainer>
            <SC.CartTabButton
              active={activeTab === "cart"}
              onClick={() => setActiveTab("cart")}
              type="button"
            >
              {`My Cart (${cartData?.length || 0})`}
            </SC.CartTabButton>
            <SC.CartTabButton
              active={activeTab === "history"}
              onClick={() => setActiveTab("history")}
              type="button"
            >
              Cart History ({cartHistoryCount || 0})
            </SC.CartTabButton>
          </SC.HeaderContainer>
          {activeTab === "history" && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginLeft: "16px",
              }}
            >
              <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                <SC.InputContiner>
                  <SC.SearchIcon />
                  <SC.InputStyle
                    value={historySearchInput}
                    onChange={handleHistorySearchChange}
                    placeholder="Search subject, sender, file..."
                    name="searchCartHistory"
                    autoComplete="off"
                  />
                  {historySearchInput && (
                    <InnoIcons.CloseIcon
                      onClick={handleHistorySearchClear}
                      style={{
                        position: "absolute",
                        right: "8px",
                        fontSize: "14px",
                        color: "#999",
                        cursor: "pointer",
                      }}
                    />
                  )}
                </SC.InputContiner>
              </form>
              <ConfigProvider
                theme={{
                  token: {
                    colorPrimary: theme.colors.themeColor,
                    colorBorder: theme.colors.themeColor,
                    borderRadius: 5,
                    fontSize: 12,
                  },
                }}
              >
                <DatePicker.RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="DD MMM YYYY"
                  allowClear
                  size="small"
                  placeholder={["From", "To"]}
                  style={{ height: "30px", width: "200px" }}
                  suffixIcon={
                    <InnoIcons.CalendarMonthIcon
                      style={{
                        fontSize: "15px",
                        color: theme.colors.themeColor,
                      }}
                    />
                  }
                />
              </ConfigProvider>
              {hasActiveFilters && (
                <button
                  onClick={handleResetFilters}
                  style={{
                    height: "30px",
                    padding: "0 12px",
                    border: `1px solid ${theme.colors.themeColor}`,
                    borderRadius: "5px",
                    background: "#fff",
                    color: theme.colors.themeColor,
                    fontSize: "12px",
                    fontWeight: 600,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <InnoIcons.RefreshIcon style={{ fontSize: "14px" }} />
                  Reset
                </button>
              )}
            </div>
          )}
        </SC.TabDiv>

        {activeTab === "cart" && (
          <div style={{ gap: "10px", display: "flex", flex: 1 }}>
            <Button
              caption={"Send"}
              onClick={handleSend}
              color={selectedItems.length <= 0 ? "black" : "white"}
              fontSize="12px"
              leftIcon={
                <InnoIcons.SendIcon
                  sx={{
                    fontSize: "16px",
                    color: selectedItems.length <= 0 ? "black" : "white",
                  }}
                />
              }
              style={{
                marginBottom: 8,
                background: selectedItems.length <= 0 ? "#cccccc" : "#0073E6",
                color: theme.colors.white,
                minWidth: "100px",
                height: "28px",
                borderRadius: "3px",
                opacity: selectedItems.length <= 0 ? "0.6" : "1",
              }}
              disabled={selectedItems.length <= 0}
            />
            <Button
              caption={isLoading ? "Downloading..." : "Download Selected"}
              onClick={handleDownload}
              color={selectedItems.length <= 0 ? "black" : "white"}
              fontSize="12px"
              leftIcon={
                <InnoIcons.DownloadIcon
                  sx={{
                    fontSize: "16px",
                    color: selectedItems.length <= 0 ? "black" : "white",
                  }}
                />
              }
              style={{
                marginBottom: 8,
                background: selectedItems.length <= 0 ? "#cccccc" : "#009406",
                color: theme.colors.white,
                minWidth: "100px",
                height: "28px",
                borderRadius: "3px",
                opacity: selectedItems.length <= 0 ? "0.6" : "1",
              }}
              disabled={isLoading || selectedItems.length <= 0}
            />
            <Button
              caption={"Remove Selected"}
              onClick={handleRemoveCart}
              color={selectedItems.length <= 0 ? "black" : "white"}
              fontSize="12px"
              leftIcon={
                <InnoIcons.DeleteIcon
                  sx={{
                    fontSize: "16px",
                    color: selectedItems.length <= 0 ? "black" : "white",
                  }}
                />
              }
              style={{
                marginBottom: 8,
                background: selectedItems.length <= 0 ? "#cccccc" : "#f05455",
                color: theme.colors.white,
                minWidth: "100px",
                height: "28px",
                borderRadius: "3px",
                opacity: selectedItems.length <= 0 ? "0.6" : "1",
              }}
              disabled={selectedItems.length <= 0}
            />
            <Button
              caption={"Clear Cart"}
              onClick={handleClearCart}
              color={cartData.length <= 0 ? "black" : "white"}
              fontSize="12px"
              leftIcon={
                <InnoIcons.RemoveShoppingCartIcon
                  sx={{
                    fontSize: "16px",
                    color: cartData.length <= 0 ? "black" : "white",
                  }}
                />
              }
              style={{
                marginBottom: 8,
                background:
                  cartData.length <= 0
                    ? "#cccccc"
                    : theme.gradient.themeGradient,
                color: theme.colors.white,
                minWidth: "100px",
                height: "28px",
                borderRadius: "3px",
                opacity: cartData.length <= 0 ? "0.6" : "1",
              }}
              disabled={cartData.length <= 0}
            />
          </div>
        )}
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          {activeTab === "cart" && selectedItems.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: "12px", fontWeight: "bold" }}>
                {`Total Selected File:${selectedItems.length}`}
              </span>
              <span style={{ fontSize: "12px", fontWeight: "bold" }}>
                {`Selected File Size:${formatFileSize(totalFileSize || 0)}`}
              </span>
            </div>
          )}

          {activeTab === "cart" && (
            <span style={{ fontSize: "12px", fontWeight: "bold" }}>
              {`Total Size:${formatFileSize(safeTotalSize)}`}
            </span>
          )}
        </div>
      </div>
      {activeTab === "cart" && (
        <SC.Container>
          <InnoTable
            tableConfig={tableConfig}
            loading={isLoading}
            padding="5px 8px"
            column={subColumnData}
            data={cartData}
            tableKey=""
            searchKeys={["propertyName", "displayTemplateName"]}
            onRowClickCallback={onRowClick}
            selectedItems={selectedItems}
            checkboxChangeCallback={checkboxChangeCallback}
            allCheckboxChangeCallback={allCheckboxChangeCallback}
          />
        </SC.Container>
      )}

      {activeTab === "history" && (
        <CartHistory
          search={historySearch}
          dateFrom={historyDateFrom}
          dateTo={historyDateTo}
          resetKey={resetKey}
          historyData={historyData}
          historyTotalRecords={historyTotalRecords}
          historyLoading={historyLoading}
          historyPage={historyPage}
          historyPageSize={historyPageSize}
          setHistoryPage={setHistoryPage}
          setHistoryPageSize={setHistoryPageSize}
          setSendTypeFilter={setSendTypeFilter}
          setSortField={setSortField}
          setSortOrder={setSortOrder}
        />
      )}

      {openModalView && (
        <SendCart
          setOpenModalView={setOpenModalView}
          openModalView={openModalView}
          files={selectedItems}
        />
      )}

      <CartOldFiles
        setOpenView={setOpenView}
        openView={openView}
        setSelectedItems={setSelectedItems}
        cartCount={cartData.length}
      />
    </>
  );
};

export default MyCart;
